"use client";

import React from "react";

/**
 * The phone half of the Mobile Application Penetration Test composition,
 * inlined from `public/images/.../Group 47.svg` so each layer can animate —
 * CSS cannot reach inside an <img>/next/image SVG.
 *
 * Layers (roles read off the exported geometry):
 *   • two stroked phone frames, the screen divider, the corner bracket and the
 *     small node circle  → drawn on with pathLength/stroke-dashoffset
 *   • four message pills  → slide in from the left, one after another
 *   • seven red data squares → pop in, then blink on a loop
 *   • fingerprint badge (red disc + 22 ridges) → scales in, then pulses a scan
 *     ring for as long as the section is in view
 *
 * `playing` starts the timeline; the component renders the finished state when
 * it is false so nothing pops if the section is scrolled past quickly, and
 * `prefers-reduced-motion` skips straight to that finished state.
 */
export default function MobileAppPhoneArt({
  playing = false,
  className,
}: {
  playing?: boolean;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 211 328"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-playing={playing ? "true" : "false"}
      aria-hidden
    >
      <mask
        id="maptPhoneMask"
        style={{ maskType: "luminance" }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="211"
        height="328"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 327.477H211V0.476562H0V327.477Z"
          fill="white"
        />
      </mask>
      <g mask="url(#maptPhoneMask)">
        {/* Phone frames + details, drawn on */}
      <path
        d="M70 68.5899V13.2663C70 7.33181 74.8637 2.47656 80.8076 2.47656H198.192C204.136 2.47656 209 7.33181 209 13.2663V244.687C209 250.621 204.136 255.477 198.192 255.477H81.8964V221.108"
        stroke="white"
        strokeWidth={4}
        className="mapt-draw mapt-frame-back"
        pathLength={1}
      />
      <path
        d="M140 255.288V314.686C140 320.621 135.136 325.477 129.192 325.477H11.8076C5.86373 325.477 1 320.621 1 314.686V83.267C1 77.3326 5.86373 72.4766 11.8076 72.4766H129.192C135.136 72.4766 140 77.3326 140 83.267V103.92"
        stroke="white"
        strokeWidth={4}
        className="mapt-draw mapt-frame-front"
        pathLength={1}
      />
      <path
        d="M1 284.977H140"
        stroke="white"
        strokeWidth={3}
        className="mapt-draw mapt-line"
        pathLength={1}
      />
      <path
        d="M62 103.477H25V166.477"
        stroke="white"
        strokeWidth={4}
        className="mapt-draw mapt-bracket"
        pathLength={1}
      />
      <path
        d="M76 191.477C76 195.342 72.8656 198.477 69 198.477C65.1344 198.477 62 195.342 62 191.477C62 187.61 65.1344 184.477 69 184.477C72.8656 184.477 76 187.61 76 191.477Z"
        fillRule="evenodd"
        clipRule="evenodd"
        stroke="white"
        strokeWidth={4}
        className="mapt-draw mapt-node"
        pathLength={1}
      />

        {/* Message pills */}
      <path
        d="M121.466 110.978H122.466C122.466 107.652 119.714 104.981 116.373 104.981V105.981V106.981C118.646 106.981 120.466 108.793 120.466 110.978H121.466ZM116.373 105.981V104.981H83.6274V105.981V106.981H116.373V105.981ZM83.6274 105.981V104.981C80.2861 104.981 77.5345 107.652 77.5345 110.978H78.5345H79.5345C79.5345 108.792 81.3545 106.981 83.6274 106.981V105.981ZM78.5345 110.978H77.5345C77.5345 114.302 80.2865 116.973 83.6274 116.973V115.973V114.973C81.3541 114.973 79.5345 113.16 79.5345 110.978H78.5345ZM83.6274 115.973V116.973H116.373V115.973V114.973H83.6274V115.973ZM116.373 115.973V116.973C119.713 116.973 122.466 114.302 122.466 110.978H121.466H120.466C120.466 113.16 118.646 114.973 116.373 114.973V115.973ZM77 110.978H78C78 107.964 80.5064 105.477 83.6274 105.477V104.477V103.477C79.4379 103.477 76 106.823 76 110.978H77ZM83.6274 104.477V105.477H116.373V104.477V103.477H83.6274V104.477ZM116.373 104.477V105.477C119.494 105.477 122 107.964 122 110.978H123H124C124 106.823 120.562 103.477 116.373 103.477V104.477ZM123 110.978H122C122 113.991 119.494 116.477 116.373 116.477V117.477V118.477C120.562 118.477 124 115.133 124 110.978H123ZM116.373 117.477V116.477H83.6274V117.477V118.477H116.373V117.477ZM83.6274 117.477V116.477C80.5058 116.477 78 113.991 78 110.978H77H76C76 115.133 79.4384 118.477 83.6274 118.477V117.477Z"
        fill="white"
        className="mapt-pill mapt-pill-1"
      />
      <path
        d="M111.468 133.978H112.468C112.468 130.652 109.72 127.98 106.379 127.98V128.98V129.98C108.65 129.98 110.468 131.792 110.468 133.978H111.468ZM106.379 128.98V127.98H83.6187V128.98V129.98H106.379V128.98ZM83.6187 128.98V127.98C80.279 127.98 77.5325 130.653 77.5325 133.978H78.5325H79.5325C79.5325 131.791 81.35 129.98 83.6187 129.98V128.98ZM78.5325 133.978H77.5325C77.5325 137.301 80.2795 139.972 83.6187 139.972V138.972V137.972C81.3495 137.972 79.5325 136.162 79.5325 133.978H78.5325ZM83.6187 138.972V139.972H106.379V138.972V137.972H83.6187V138.972ZM106.379 138.972V139.972C109.719 139.972 112.468 137.301 112.468 133.978H111.468H110.468C110.468 136.161 108.65 137.972 106.379 137.972V138.972ZM77 133.978H78C78 130.963 80.5038 128.477 83.6187 128.477V127.477V126.477C79.4327 126.477 76 129.825 76 133.978H77ZM83.6187 127.477V128.477H106.379V127.477V126.477H83.6187V127.477ZM106.379 127.477V128.477C109.494 128.477 112 130.964 112 133.978H113H114C114 129.825 110.564 126.477 106.379 126.477V127.477ZM113 133.978H112C112 136.992 109.495 139.477 106.379 139.477V140.477V141.477C110.564 141.477 114 138.132 114 133.978H113ZM106.379 140.477V139.477H83.6187V140.477V141.477H106.379V140.477ZM83.6187 140.477V139.477C80.5032 139.477 78 136.993 78 133.978H77H76C76 138.132 79.4333 141.477 83.6187 141.477V140.477Z"
        fill="white"
        className="mapt-pill mapt-pill-2"
      />
      <path
        d="M139.483 157.975H140.483C140.483 154.661 137.767 151.981 134.444 151.981V152.981V153.981C136.679 153.981 138.483 155.783 138.483 157.975H139.483ZM134.444 152.981V151.981H83.5521V152.981V153.981H134.444V152.981ZM83.5521 152.981V151.981C80.2322 151.981 77.5164 154.662 77.5164 157.975H78.5164H79.5164C79.5164 155.782 81.3209 153.981 83.5521 153.981V152.981ZM78.5164 157.975H77.5164C77.5164 161.291 80.2317 163.973 83.5521 163.973V162.973V161.973C81.3214 161.973 79.5164 160.171 79.5164 157.975H78.5164ZM83.5521 162.973V163.973H134.444V162.973V161.973H83.5521V162.973ZM134.444 162.973V163.973C137.767 163.973 140.483 161.291 140.483 157.975H139.483H138.483C138.483 160.171 136.678 161.973 134.444 161.973V162.973ZM77 157.975H78C78 154.951 80.4833 152.477 83.5521 152.477V151.477V150.477C79.3942 150.477 76 153.831 76 157.975H77ZM83.5521 151.477V152.477H134.444V151.477V150.477H83.5521V151.477ZM134.444 151.477V152.477C137.514 152.477 140 154.952 140 157.975H141H142C142 153.83 138.602 150.477 134.444 150.477V151.477ZM141 157.975H140C140 160.999 137.514 163.477 134.444 163.477V164.477V165.477C138.603 165.477 142 162.119 142 157.975H141ZM134.444 164.477V163.477H83.5521V164.477V165.477H134.444V164.477ZM83.5521 164.477V163.477C80.4838 163.477 78 161 78 157.975H77H76C76 162.118 79.3936 165.477 83.5521 165.477V164.477Z"
        fill="white"
        className="mapt-pill mapt-pill-3"
      />
      <path
        d="M151.482 181.975H152.482C152.482 178.661 149.763 175.981 146.439 175.981V176.981V177.981C148.676 177.981 150.482 179.783 150.482 181.975H151.482ZM146.439 176.981V175.981H83.5575V176.981V177.981H146.439V176.981ZM83.5575 176.981V175.981C80.2359 175.981 77.5177 178.661 77.5177 181.975H78.5177H79.5177C79.5177 179.783 81.3234 177.981 83.5575 177.981V176.981ZM78.5177 181.975H77.5177C77.5177 185.291 80.2355 187.973 83.5575 187.973V186.973V185.973C81.3238 185.973 79.5177 184.171 79.5177 181.975H78.5177ZM83.5575 186.973V187.973H146.439V186.973V185.973H83.5575V186.973ZM146.439 186.973V187.973C149.763 187.973 152.482 185.292 152.482 181.975H151.482H150.482C150.482 184.17 148.676 185.973 146.439 185.973V186.973ZM77 181.975H78C78 178.952 80.4849 176.477 83.5575 176.477V175.477V174.477C79.3974 174.477 76 177.83 76 181.975H77ZM83.5575 175.477V176.477H146.439V175.477V174.477H83.5575V175.477ZM146.439 175.477V176.477C149.513 176.477 152 178.953 152 181.975H153H154C154 177.83 150.599 174.477 146.439 174.477V175.477ZM153 181.975H152C152 184.999 149.512 187.477 146.439 187.477V188.477V189.477C150.599 189.477 154 186.121 154 181.975H153ZM146.439 188.477V187.477H83.5575V188.477V189.477H146.439V188.477ZM83.5575 188.477V187.477C80.4854 187.477 78 184.999 78 181.975H77H76C76 186.12 79.3968 189.477 83.5575 189.477V188.477Z"
        fill="white"
        className="mapt-pill mapt-pill-4"
      />

        {/* Data squares */}
      <path
        d="M15 212.477H24V203.477H15V212.477Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#EC1938"
        className="mapt-dot mapt-dot-1"
      />
      <path
        d="M33 164.477H42V155.477H33V164.477Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#EC1938"
        className="mapt-dot mapt-dot-2"
      />
      <path
        d="M33 202.477H42V193.477H33V202.477Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#EC1938"
        className="mapt-dot mapt-dot-3"
      />
      <path
        d="M46 202.477H55V193.477H46V202.477Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#EC1938"
        className="mapt-dot mapt-dot-4"
      />
      <path
        d="M33 221.477H42V212.477H33V221.477Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#EC1938"
        className="mapt-dot mapt-dot-5"
      />
      <path
        d="M46 221.477H55V212.477H46V221.477Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#EC1938"
        className="mapt-dot mapt-dot-6"
      />
      <path
        d="M16.771 240.705H24.2282V233.249H16.771V240.705ZM15 242.477H26V231.477H15V242.477Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#EC1938"
        className="mapt-dot mapt-dot-7"
      />

        {/* Home dot */}
      <path
        d="M70.9961 313.477H69.0031C65.1514 313.477 62 310.325 62 306.473V304.48C62 300.629 65.1514 297.477 69.0031 297.477H70.9961C74.8478 297.477 78 300.629 78 304.48V306.473C78 310.325 74.8478 313.477 70.9961 313.477Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#EC1938"
        className="mapt-home"
      />

        {/* Fingerprint badge: disc, scan ring, ridges */}
      <path
        d="M175 251.976C175 270.478 160.002 285.477 141.5 285.477C122.999 285.477 108 270.478 108 251.976C108 233.475 122.999 218.477 141.5 218.477C160.002 218.477 175 233.475 175 251.976Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#EC1938"
        className="mapt-scan-disc"
      />
        <circle
          className="mapt-scan-ring mapt-scan-ring-1"
          cx="141.5"
          cy="252"
          r="33.5"
          fill="none"
          stroke="#EC1938"
          strokeWidth={2}
        />
        <circle
          className="mapt-scan-ring mapt-scan-ring-2"
          cx="141.5"
          cy="252"
          r="33.5"
          fill="none"
          stroke="#EC1938"
          strokeWidth={2}
        />
      <path
        d="M121.853 261.892C121.438 261.892 121.043 261.649 120.871 261.241C119.692 258.457 119.094 255.499 119.094 252.451C119.094 251.863 119.572 251.385 120.16 251.385C120.749 251.385 121.228 251.863 121.228 252.451C121.228 255.211 121.768 257.89 122.834 260.411C123.063 260.952 122.809 261.579 122.267 261.809C122.132 261.865 121.992 261.892 121.853 261.892Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M160.847 264.397C160.695 264.397 160.539 264.364 160.394 264.297C159.861 264.045 159.632 263.411 159.883 262.876C160.796 260.936 161.394 258.876 161.659 256.755C161.734 256.17 162.267 255.756 162.85 255.831C163.435 255.904 163.85 256.438 163.776 257.022C163.481 259.366 162.82 261.64 161.811 263.785C161.63 264.171 161.247 264.397 160.847 264.397Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M163.191 253.516C162.603 253.516 162.126 253.038 162.126 252.449C162.126 244.213 157.224 236.816 149.636 233.607C149.094 233.377 148.839 232.751 149.069 232.21C149.299 231.668 149.924 231.413 150.466 231.644C158.844 235.187 164.258 243.354 164.258 252.449C164.258 253.038 163.78 253.516 163.191 253.516Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M121.353 246.445C121.236 246.445 121.118 246.424 121.002 246.384C120.446 246.193 120.151 245.585 120.345 245.029C123.5 235.96 132.073 229.869 141.678 229.869C142.266 229.869 142.745 230.346 142.745 230.935C142.745 231.524 142.266 232.002 141.678 232.002C132.98 232.002 125.214 237.517 122.359 245.728C122.206 246.168 121.793 246.445 121.353 246.445Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M124.59 255.614C124.058 255.614 123.598 255.218 123.533 254.677C123.444 253.945 123.398 253.195 123.398 252.451C123.398 245.968 126.884 239.91 132.494 236.645C133.003 236.349 133.656 236.521 133.951 237.03C134.248 237.539 134.075 238.191 133.567 238.487C128.609 241.372 125.532 246.723 125.532 252.451C125.532 253.109 125.57 253.773 125.65 254.421C125.721 255.004 125.304 255.536 124.72 255.608C124.675 255.613 124.633 255.614 124.59 255.614Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M152.252 239.939C152.022 239.939 151.792 239.863 151.598 239.712C148.74 237.483 145.309 236.305 141.675 236.305C140.894 236.305 140.108 236.361 139.341 236.471C138.758 236.557 138.217 236.152 138.132 235.57C138.049 234.988 138.452 234.446 139.035 234.362C139.903 234.236 140.792 234.172 141.675 234.172C145.789 234.172 149.672 235.507 152.907 238.031C153.371 238.393 153.456 239.064 153.094 239.528C152.883 239.795 152.57 239.939 152.252 239.939Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M152.995 271.817C152.758 271.817 152.516 271.737 152.32 271.574C151.864 271.202 151.799 270.529 152.171 270.076C157.71 263.319 160.37 251.598 155.233 243.677C154.912 243.184 155.053 242.524 155.547 242.204C156.041 241.883 156.701 242.023 157.021 242.517C159.646 246.562 160.653 251.868 159.854 257.454C159.129 262.542 156.929 267.635 153.82 271.427C153.609 271.684 153.303 271.817 152.995 271.817Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M142.78 259.997C142.208 259.997 141.736 259.546 141.714 258.972C141.638 256.915 141.344 254.925 140.845 253.059C140.692 252.49 141.031 251.907 141.599 251.753C142.168 251.602 142.751 251.939 142.903 252.508C143.445 254.529 143.761 256.678 143.845 258.892C143.866 259.479 143.409 259.975 142.82 259.997H142.78Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M140.304 275.034C140.172 275.034 140.038 275.01 139.909 274.956C139.362 274.738 139.096 274.119 139.315 273.571C140.729 270.036 141.612 266.245 141.869 262.608C141.912 262.022 142.42 261.579 143.008 261.62C143.596 261.663 144.037 262.172 143.996 262.759C143.724 266.614 142.79 270.626 141.294 274.363C141.126 274.781 140.726 275.034 140.304 275.034Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M124.845 266.915C124.747 266.915 124.648 266.903 124.551 266.874C123.984 266.711 123.657 266.121 123.819 265.555C124.405 263.51 124.571 261.258 124.297 259.044C124.224 258.46 124.64 257.927 125.224 257.856C125.809 257.783 126.341 258.199 126.414 258.783C126.721 261.28 126.533 263.825 125.868 266.142C125.733 266.61 125.307 266.915 124.845 266.915Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M154.714 253.517C154.127 253.517 153.649 253.039 153.649 252.45C153.649 248.501 151.702 244.808 148.441 242.571C147.955 242.237 147.83 241.573 148.164 241.09C148.497 240.603 149.162 240.479 149.645 240.811C153.487 243.446 155.781 247.797 155.781 252.45C155.781 253.039 155.304 253.517 154.714 253.517Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M128.642 253.517C128.052 253.517 127.574 253.039 127.574 252.45C127.574 244.675 133.902 238.346 141.678 238.346C142.162 238.346 142.649 238.37 143.125 238.422C143.711 238.481 144.138 239.005 144.079 239.588C144.019 240.175 143.497 240.6 142.909 240.541C142.504 240.499 142.09 240.479 141.678 240.479C135.077 240.479 129.707 245.85 129.707 252.45C129.707 253.039 129.23 253.517 128.642 253.517Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M128.304 270.373C128.18 270.373 128.054 270.35 127.929 270.303C127.379 270.098 127.099 269.483 127.307 268.933C128.171 266.624 128.612 264.191 128.612 261.701C128.612 260.075 128.419 258.454 128.044 256.887C127.908 256.313 128.26 255.738 128.833 255.602C129.406 255.465 129.98 255.817 130.118 256.39C130.533 258.12 130.742 259.907 130.742 261.701C130.742 264.448 130.259 267.132 129.303 269.679C129.143 270.109 128.736 270.373 128.304 270.373Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M144.508 275.106C144.169 275.106 143.836 274.944 143.629 274.643C143.295 274.16 143.418 273.495 143.903 273.16C149.283 269.464 152.73 263.802 153.615 257.221C153.694 256.638 154.229 256.227 154.815 256.307C155.397 256.385 155.808 256.921 155.73 257.506C154.762 264.691 150.992 270.876 145.112 274.918C144.926 275.045 144.716 275.106 144.508 275.106Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M134.007 249.52C133.84 249.52 133.673 249.481 133.513 249.4C132.992 249.127 132.79 248.482 133.063 247.961C134.745 244.739 138.046 242.738 141.677 242.738C144.101 242.738 146.424 243.638 148.214 245.268C148.65 245.666 148.681 246.339 148.285 246.774C147.888 247.21 147.214 247.24 146.78 246.845C145.381 245.571 143.569 244.87 141.677 244.87C138.843 244.87 136.265 246.432 134.953 248.946C134.763 249.312 134.391 249.52 134.007 249.52Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M134.39 263.814C133.801 263.814 133.323 263.338 133.323 262.75C133.316 259.701 132.902 256.672 132.093 253.745C131.937 253.178 132.271 252.591 132.838 252.434C133.407 252.276 133.992 252.61 134.148 253.178C135.009 256.287 135.449 259.507 135.456 262.746C135.457 263.335 134.98 263.814 134.392 263.814H134.39Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M132.363 272.891C132.215 272.891 132.065 272.86 131.923 272.795C131.387 272.553 131.149 271.92 131.392 271.385C132.091 269.847 132.582 268.222 132.855 266.561C132.953 265.98 133.498 265.584 134.081 265.68C134.661 265.776 135.056 266.324 134.96 266.906C134.657 268.754 134.11 270.557 133.333 272.266C133.155 272.658 132.768 272.891 132.363 272.891Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M151.23 259.04C151.207 259.04 151.185 259.039 151.161 259.038C150.574 259 150.127 258.494 150.165 257.906C150.323 255.397 149.986 252.932 149.162 250.58C148.967 250.027 149.261 249.418 149.815 249.222C150.371 249.027 150.98 249.322 151.174 249.877C152.093 252.497 152.468 255.246 152.293 258.041C152.258 258.606 151.788 259.04 151.23 259.04Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M136.121 274.308C135.976 274.308 135.828 274.278 135.688 274.215C135.15 273.976 134.907 273.346 135.147 272.809C135.648 271.685 136.076 270.515 136.416 269.334C136.581 268.77 137.172 268.443 137.737 268.607C138.302 268.77 138.628 269.361 138.465 269.928C138.096 271.201 137.635 272.464 137.095 273.676C136.919 274.073 136.529 274.308 136.121 274.308Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M138.14 267.362C138.087 267.362 138.034 267.358 137.98 267.351C137.398 267.262 136.998 266.719 137.085 266.136C137.337 264.467 137.41 262.76 137.302 261.06C137.252 260.251 137.159 259.435 137.026 258.64C136.93 258.057 137.323 257.508 137.903 257.413C138.483 257.316 139.033 257.709 139.129 258.29C139.274 259.157 139.375 260.044 139.432 260.924C139.547 262.775 139.468 264.636 139.194 266.455C139.114 266.984 138.659 267.362 138.14 267.362Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M147.498 260.399C146.909 260.399 146.433 259.921 146.433 259.332C146.433 255.757 145.439 248.999 141.678 248.999C139.775 248.999 138.225 250.546 138.225 252.451C138.225 252.517 138.227 252.58 138.23 252.644C138.263 253.232 137.814 253.735 137.226 253.768C136.631 253.799 136.134 253.351 136.102 252.763C136.096 252.66 136.094 252.556 136.094 252.451C136.094 249.371 138.599 246.865 141.678 246.865C146.761 246.865 148.564 253.58 148.564 259.332C148.564 259.921 148.086 260.399 147.498 260.399Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      <path
        d="M145.465 269.995C145.32 269.995 145.172 269.965 145.032 269.903C144.494 269.663 144.251 269.034 144.491 268.494C145.299 266.685 145.862 264.77 146.166 262.81C146.255 262.228 146.8 261.829 147.382 261.919C147.964 262.008 148.362 262.553 148.271 263.136C147.939 265.283 147.323 267.379 146.439 269.363C146.263 269.759 145.873 269.995 145.465 269.995Z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="white"
        className="mapt-ridge"
      />
      </g>

      <style jsx>{`
        /* Layers start hidden and are revealed by the timeline below. The
           finished state is what a reduced-motion visitor sees straight away. */
        .mapt-draw {
          stroke-dasharray: 1;
          stroke-dashoffset: 1;
        }
        .mapt-pill,
        .mapt-dot,
        .mapt-home,
        .mapt-scan-disc,
        .mapt-ridge,
        .mapt-scan-ring {
          opacity: 0;
        }
        .mapt-pill,
        .mapt-dot,
        .mapt-scan-disc,
        .mapt-scan-ring {
          transform-box: fill-box;
          transform-origin: center;
        }

        /* 1. The two frames draw themselves, back one first. */
        svg[data-playing="true"] .mapt-frame-back {
          animation: maptDraw 760ms cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        svg[data-playing="true"] .mapt-frame-front {
          animation: maptDraw 760ms cubic-bezier(0.4, 0, 0.2, 1) 160ms both;
        }
        /* 2. Screen divider, corner bracket, node circle. */
        svg[data-playing="true"] .mapt-line {
          animation: maptDraw 320ms cubic-bezier(0.4, 0, 0.2, 1) 700ms both;
        }
        svg[data-playing="true"] .mapt-bracket {
          animation: maptDraw 380ms cubic-bezier(0.4, 0, 0.2, 1) 780ms both;
        }
        svg[data-playing="true"] .mapt-node {
          animation: maptDraw 320ms cubic-bezier(0.4, 0, 0.2, 1) 880ms both;
        }
        svg[data-playing="true"] .mapt-home {
          animation: maptFade 300ms ease-out 900ms both;
        }

        /* 3. Message pills arrive one after another, like traffic being read. */
        svg[data-playing="true"] .mapt-pill {
          animation: maptPillIn 420ms cubic-bezier(0.2, 0.7, 0.3, 1) both;
        }
        svg[data-playing="true"] .mapt-pill-1 {
          animation-delay: 940ms;
        }
        svg[data-playing="true"] .mapt-pill-2 {
          animation-delay: 1030ms;
        }
        svg[data-playing="true"] .mapt-pill-3 {
          animation-delay: 1120ms;
        }
        svg[data-playing="true"] .mapt-pill-4 {
          animation-delay: 1210ms;
        }

        /* 4. Data squares pop in, then keep blinking at their own pace. */
        svg[data-playing="true"] .mapt-dot {
          animation: maptDotIn 300ms cubic-bezier(0.2, 0.7, 0.3, 1) both,
            maptDotBlink 3200ms ease-in-out infinite;
        }
        svg[data-playing="true"] .mapt-dot-1 {
          animation-delay: 1180ms, 2100ms;
        }
        svg[data-playing="true"] .mapt-dot-2 {
          animation-delay: 1240ms, 2500ms;
        }
        svg[data-playing="true"] .mapt-dot-3 {
          animation-delay: 1300ms, 2900ms;
        }
        svg[data-playing="true"] .mapt-dot-4 {
          animation-delay: 1360ms, 2300ms;
        }
        svg[data-playing="true"] .mapt-dot-5 {
          animation-delay: 1420ms, 3100ms;
        }
        svg[data-playing="true"] .mapt-dot-6 {
          animation-delay: 1480ms, 2700ms;
        }
        svg[data-playing="true"] .mapt-dot-7 {
          animation-delay: 1540ms, 3400ms;
        }

        /* 5. Fingerprint badge lands with a small overshoot, then scans. */
        svg[data-playing="true"] .mapt-scan-disc {
          animation: maptDiscIn 520ms cubic-bezier(0.2, 0.7, 0.3, 1) 1320ms both,
            maptDiscGlow 2800ms ease-in-out 2000ms infinite;
        }
        svg[data-playing="true"] .mapt-ridge {
          animation: maptFade 420ms ease-out 1520ms both;
        }
        /* Two rings, half a beat apart, so the badge reads as a scanner rather
           than a single blip. They fade in only once they clear the red disc —
           red on red is invisible. */
        svg[data-playing="true"] .mapt-scan-ring-1 {
          animation: maptScan 2600ms ease-out 1900ms infinite;
        }
        svg[data-playing="true"] .mapt-scan-ring-2 {
          animation: maptScan 2600ms ease-out 2600ms infinite;
        }

        @keyframes maptDraw {
          from {
            stroke-dashoffset: 1;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes maptFade {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes maptPillIn {
          from {
            opacity: 0;
            transform: translateX(-12px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes maptDotIn {
          from {
            opacity: 0;
            transform: scale(0.2);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes maptDotBlink {
          0% {
            opacity: 1;
          }
          45% {
            opacity: 0.2;
          }
          100% {
            opacity: 1;
          }
        }
        @keyframes maptDiscIn {
          0% {
            opacity: 0;
            transform: scale(0.55);
          }
          70% {
            opacity: 1;
            transform: scale(1.07);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes maptDiscGlow {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.82;
          }
          100% {
            opacity: 1;
          }
        }
        @keyframes maptScan {
          0% {
            opacity: 0;
            transform: scale(1);
          }
          14% {
            opacity: 0.8;
          }
          70% {
            opacity: 0;
            transform: scale(1.72);
          }
          100% {
            opacity: 0;
            transform: scale(1.72);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .mapt-draw {
            stroke-dashoffset: 0;
          }
          .mapt-pill,
          .mapt-dot,
          .mapt-home,
          .mapt-scan-disc,
          .mapt-ridge {
            opacity: 1;
          }
          svg[data-playing="true"] .mapt-draw,
          svg[data-playing="true"] .mapt-pill,
          svg[data-playing="true"] .mapt-dot,
          svg[data-playing="true"] .mapt-home,
          svg[data-playing="true"] .mapt-scan-disc,
          svg[data-playing="true"] .mapt-ridge,
          svg[data-playing="true"] .mapt-scan-ring {
            animation: none !important;
          }
        }
      `}</style>
    </svg>
  );
}
